import { useMemo } from 'react';
import { Box, Skeleton, useTheme } from '@mui/material';
import {
  IconSelector,
  IIconSelectorValue,
  useEditorStore,
  useFlowBuilder2Context,
  useFlowBuilder2FlowValidators,
  FlowBuilder2FlowFieldNamesEnum,
  useAssistantLoadingStore,
} from '@diana/core';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { useEntityFieldState } from '../../Support/useEntityFieldState';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { isEqual } from 'lodash';

export const FlowIconSelector = (props: { hasWorkflowRequest?: boolean }) => {
  const { hasWorkflowRequest } = props;
  const { updateAndSaveFlow } = useFlowBuilder2Context();

  const getFlow = useEditorStore((s) => s.getFlow);
  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const flowIcon = useEditorStore((s) => s.flow?.properties.icon);
  const flowImageUrl = useEditorStore((s) => s.flow?.properties.imageUrl);
  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);
  const disabled = hasAnyPendingStatus || isChatLoading;

  const { palette } = useTheme();
  const { isLoading: isPageLoading, error: pageError } = useFlowBuilder2PageContext();

  const validators = useFlowBuilder2FlowValidators();
  const fieldState = useEntityFieldState({
    objectId: flowId,
    objectType: 'flow',
    fieldName: FlowBuilder2FlowFieldNamesEnum.Icon,
  });

  const validateIcon = validators[FlowBuilder2FlowFieldNamesEnum.Icon];
  const isEmpty = !flowIcon?.color && !flowIcon?.shape;

  const value = useMemo<IIconSelectorValue>(() => {
    if (!flowIcon)
      return {
        color: palette.background.paper,
        icon: 'AddOutlined',
        iconColor: palette.text.secondary,
        imageUrl: flowImageUrl ?? undefined,
      };

    return {
      color: flowIcon.color,
      icon: flowIcon.shape,
      imageUrl: flowImageUrl ?? undefined,
    };
  }, [flowIcon, flowImageUrl, palette]);

  const error = useMemo(() => {
    const flow = getFlow();
    if (!flow || (!fieldState.touched && isEmpty)) return null;
    return validateIcon({ flow });
  }, [getFlow, fieldState, isEmpty, validateIcon]);

  if (isPageLoading || hasWorkflowRequest)
    return (
      <Box>
        <Skeleton variant="rounded" width={30} height={30} />
      </Box>
    );

  if (!pageError)
    return (
      <Box
        sx={({ palette }) => ({
          button: { borderColor: error ? palette.error.main : palette.divider },
        })}
      >
        <IconSelector
          readOnly={disabled}
          size={32}
          iconSize={20}
          value={value}
          onChange={({ color, icon: shape, imageUrl }) => {
            fieldState.emitDirty();
            fieldState.emitTouched();
            updateAndSaveFlow((draft) => {
              draft.properties.imageUrl = imageUrl ?? draft.properties.imageUrl;
              draft.properties.icon = { color, shape };
              return draft;
            });
          }}
        />
      </Box>
    );

  return null;
};
